import type { CompetitorDto } from '@reelplicate/api-client/model';
import { z } from 'zod';

import { siteHost } from '@/features/onboarding/api-values';
import { handle, website } from '@/features/onboarding/schemas';

export const ACCOUNT_PLATFORMS = ['TIKTOK', 'INSTAGRAM'] as const;
export type AccountPlatform = (typeof ACCOUNT_PLATFORMS)[number];

export const PLATFORM_LABEL: Record<AccountPlatform, string> = {
  TIKTOK: 'TikTok',
  INSTAGRAM: 'Instagram',
};

const AccountSchema = z.object({ platform: z.enum(ACCOUNT_PLATFORMS), handle });

const CompetitorSchema = z.object({
  /** The saved competitor's, so saving updates it in place. */
  id: z.string().optional(),
  website: website('Enter a website like oatly.com'),
  accounts: z.array(AccountSchema),
});

export type AccountValues = z.infer<typeof AccountSchema>;
export type CompetitorValues = z.infer<typeof CompetitorSchema>;
export type CompetitorsFormValues = { competitors: CompetitorValues[] };

/** A row with nothing in it: ignored when saving, so never an error. */
export const isBlank = (c: CompetitorValues) =>
  !c.website.trim() && c.accounts.every((a) => !a.handle.trim());

/**
 * The competitors form (Settings and onboarding): each competitor's site
 * and any number of TikTok / Instagram accounts. Blank rows are dropped on
 * save; at least one competitor has to be left.
 */
export const CompetitorsFormSchema = z
  .object({ competitors: z.array(CompetitorSchema) })
  .refine((v) => v.competitors.some((c) => !isBlank(c)), {
    path: ['competitors', 0, 'website'],
    message: 'Add a competitor’s website or one of their accounts',
  });

export const blankAccount = (platform: AccountPlatform = 'TIKTOK'): AccountValues => ({
  platform,
  handle: '',
});
export const blankCompetitor = (): CompetitorValues => ({
  website: '',
  accounts: [blankAccount()],
});

/** Accounts filled in across the form - what the plan's limit counts. */
export const accountsUsed = (competitors: CompetitorValues[]) =>
  competitors.reduce(
    (sum, c) => sum + c.accounts.filter((a) => a.handle.trim()).length,
    0,
  );

/** Saved competitors as form values. */
export const toCompetitorValues = (items: CompetitorDto[]): CompetitorValues[] =>
  items.map((c) => ({
    id: c.id,
    website: c.website ? siteHost(c.website) : '',
    accounts: c.accounts.flatMap((a) =>
      a.platform === 'TIKTOK' || a.platform === 'INSTAGRAM'
        ? [{ platform: a.platform, handle: `@${a.handle}` }]
        : [],
    ),
  }));

/** The form as the API's body: blank rows and blank accounts left out. */
export const toCompetitorInputs = (competitors: CompetitorValues[]) =>
  competitors
    .filter((c) => !isBlank(c))
    .map((c) => ({
      ...(c.id ? { id: c.id } : {}),
      ...(c.website.trim() ? { website: c.website } : {}),
      accounts: c.accounts
        .filter((a) => a.handle.trim())
        .map((a) => ({ platform: a.platform, handle: a.handle })),
    }));

export { linkPlatform } from '@/features/accounts/platforms';
